import { defineStore } from 'pinia'
import { persistConfig } from '../index'

/**
 * Settings Store
 * 管理用户的设置和首选项
 */
export const useSettingsStore = defineStore('settings', {
  state: () => ({
    // 主题设置
    theme: {
      // 主题模式: 'light', 'dark', 'system'
      mode: 'system',
      // 主题颜色
      primaryColor: '#409eff',
      // 自定义主题颜色
      customColors: {},
      // 暗色主题是否启用
      darkMode: false
    },
    
    // 界面设置
    interface: {
      // 语言设置
      language: 'zh-CN',
      // 是否显示侧边栏
      showSidebar: true,
      // 侧边栏折叠状态
      sidebarCollapsed: false,
      // 是否显示面包屑
      showBreadcrumb: true,
      // 顶部导航栏是否固定
      fixedHeader: true,
      // 标签页导航
      tabNavigation: {
        enabled: true,
        cache: true,
        showCloseButtons: true
      }
    },
    
    // 通知设置
    notifications: {
      // 声音通知
      soundEnabled: true,
      // 桌面通知
      desktopEnabled: true,
      // 通知类型开关
      types: {
        success: true,
        warning: true,
        error: true,
        info: true
      }
    },
    
    // 性能设置
    performance: {
      // 动画效果开关
      animations: true,
      // 懒加载开关
      lazyLoading: true,
      // 数据缓存时间 (秒)
      cacheTime: 300
    },
    
    // 快捷键设置
    shortcuts: {
      // 是否启用快捷键
      enabled: true,
      // 自定义快捷键映射
      customMappings: {}
    },
    
    // 最后更新时间
    lastUpdated: Date.now()
  }),
  
  getters: {
    // 获取当前主题模式
    currentThemeMode: (state) => {
      if (state.theme.mode === 'system') {
        // 检测系统主题
        const isSystemDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        return isSystemDark ? 'dark' : 'light'
      }
      return state.theme.mode
    },
    
    // 是否使用暗色主题
    isDarkTheme: (state) => {
      return state.theme.darkMode || (state.theme.mode === 'system' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
    },
    
    // 获取有效的语言设置
    effectiveLanguage: (state) => {
      // 检查语言是否可用
      const availableLanguages = ['zh-CN', 'en-US']
      return availableLanguages.includes(state.interface.language) ? state.interface.language : 'zh-CN'
    }
  },
  
  actions: {
    // 设置主题模式
    setThemeMode(mode) {
      this.theme.mode = mode
      this.updateDarkModeStatus()
    },
    
    // 更新暗色模式状态
    updateDarkModeStatus() {
      const isDark = this.isDarkTheme
      this.theme.darkMode = isDark
      
      // 更新HTML类名
      document.documentElement.classList.toggle('dark', isDark)
    },
    
    // 设置主色调
    setPrimaryColor(color) {
      this.theme.primaryColor = color
      // 可以在这里添加主题色应用逻辑
    },
    
    // 切换侧边栏显示状态
    toggleSidebar() {
      this.interface.showSidebar = !this.interface.showSidebar
    },
    
    // 切换侧边栏折叠状态
    toggleSidebarCollapsed() {
      this.interface.sidebarCollapsed = !this.interface.sidebarCollapsed
    },
    
    // 更新语言设置
    setLanguage(language) {
      this.interface.language = language
      // 可以在这里添加语言切换逻辑
    },
    
    // 设置通知类型开关
    setNotificationTypeEnabled(type, enabled) {
      if (this.notifications.types.hasOwnProperty(type)) {
        this.notifications.types[type] = enabled
      }
    },
    
    // 保存自定义设置
    saveSetting(category, key, value) {
      if (this[category] && typeof this[category][key] !== 'undefined') {
        this[category][key] = value
        this.lastUpdated = Date.now()
        return true
      }
      return false
    },
    
    // 重置设置到默认值
    resetSettings() {
      const defaultState = this.$reset()
      this.lastUpdated = Date.now()
      this.updateDarkModeStatus()
    },
    
    // 初始化设置
    async initialize() {
      try {
        // 监听系统主题变化
        if (window.matchMedia) {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
          mediaQuery.addEventListener('change', () => {
            if (this.theme.mode === 'system') {
              this.updateDarkModeStatus()
            }
          })
        }
        
        // 应用暗色模式
        this.updateDarkModeStatus()
        
      } catch (error) {
        console.error('Failed to initialize settings:', error)
      }
    }
  },
  
  // 配置持久化
  persist: persistConfig.createPersistConfig('settings', {
    // 持久化所有设置
    paths: ['theme', 'interface', 'notifications', 'performance', 'shortcuts']
  })
})